/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom, Subject} from 'rxjs';
import {Component, Inject, OnInit, signal} from '@angular/core';
import {completeCopyModal} from '../../common/util/complete-copy-modal';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {createResourceCopy} from '../../../components/util/create-resource-copy';
import {nextCopyTitle} from '../../../components/util/next-copy-title';
import {Uuid} from '../../../components/util/uuid';
import {LayoutApiService} from './layout-api.service';

export interface LayoutCopyResult {
  model: any;
  destination: boolean;
}

@Component({
  standalone: false,
  selector: 'adn-layout-copy-modal',
  template: `
    <form class="form-horizontal" (ngSubmit)="copy()">
      <div class="modal-header dialogAddStuff">
        <button type="button" class="close" (click)="dismiss()">&times;</button>
        <h4 class="modal-title">
          <span>{{ 'label.copy.layout' | ogTranslate }}</span>
          <small>{{originalName}}</small>
        </h4>
      </div>
      <div class="modal-body">
        @if (copyFailed()) {
          <p class="alert alert-danger">The layout could not be copied. Try again.</p>
        }
        <div class="form-group">
          <label class="col-md-3 control-label">Name</label>
          <div class="col-md-9">
            <input type="text" class="form-control" name="layout.name" [(ngModel)]="name" required>
          </div>
        </div>
        <div class="form-group">
          <label class="col-md-3 control-label">Description</label>
          <div class="col-md-9">
            <input type="text" class="form-control" name="layout.description" [(ngModel)]="description">
          </div>
        </div>
        <div class="checkbox text-right">
          <label class="checkbox">
            <input type="checkbox" [(ngModel)]="destination" name="layout.destination">
            <span class="small">Go to new layout after copy</span>
          </label>
        </div>
      </div>
      <div class="modal-footer">
        <a class="btn btn-link btn-xs" (click)="dismiss()">{{ 'button.cancel' | ogTranslate }}</a>
        <button type="submit" class="btn btn-default" [disabled]="pending()">{{ 'label.copy' | ogTranslate }}</button>
      </div>
    </form>
  `
})
export class LayoutCopyModalComponent implements OnInit {
  /** Set via BsModalService initialState */
  model: any;

  readonly onCopied = new Subject<LayoutCopyResult>();

  originalName = '';
  name = '';
  description = '';
  destination = true;
  readonly pending = signal(false);
  readonly copyFailed = signal(false);

  constructor(
    public bsModalRef: BsModalRef,
    @Inject(LayoutApiService) private readonly Layout: LayoutApiService
  ) {}

  ngOnInit(): void {
    this.originalName = this.model?.name || '';
    this.name = nextCopyTitle(this.model?.name || '');
    this.description = this.model?.description || '';
  }

  copy(): void {
    if (this.pending()) {
      return;
    }
    this.pending.set(true);
    this.copyFailed.set(false);
    const layout = createResourceCopy(this.model, {
      id: Uuid.generate(),
      name: this.name,
      description: this.description
    });
    firstValueFrom(this.Layout.save(layout)).then((saved: any) => {
      completeCopyModal(this.bsModalRef, this.onCopied, {model: saved, destination: this.destination});
    }, () => {
      this.copyFailed.set(true);
      this.pending.set(false);
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }
}
